import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Vehicle = {
  uuid: string; make_model: string; registration: string | null; colour: string | null;
  owner_user: string | null; status: string; acquired_date: string | null;
  disposed_date: string | null; notes: string | null; ledger_seq: number;
};

export function Vehicles() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showAdd, setShowAdd] = useState(false);

  async function load() {
    try { setVehicles((await fetch('/api/vehicles').then((r) => r.json())).vehicles ?? []); } catch { /* offline */ }
  }
  useEffect(() => { load(); }, []);

  async function dispose(uuid: string, makeModel: string) {
    if (!confirm(`Mark "${makeModel}" as disposed? This cannot be undone.`)) return;
    await fetch(`/api/vehicles/${uuid}`, {
      method: 'PATCH', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ status: 'disposed' }),
    });
    await load();
  }

  return (
    <Page
      eyebrow="Records"
      title="Vehicles"
      actions={<button className="btn btn-primary" type="button" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Close' : '+ Add vehicle'}</button>}
    >
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Record any vehicle you own, regularly drive, or have access to. Vehicle information is{' '}
          <b>not mandatory</b> on notification forms — but keeping this list current helps you stay
          accurate when reporting and gives inspectors a complete picture.
        </p>
      </section>

      {showAdd && <AddForm onDone={() => { setShowAdd(false); load(); }} />}

      <div className="card">
        <div className="panel-head"><h3>Vehicles</h3><span className="hint">{vehicles.length}</span></div>
        <div className="ledger">
          {vehicles.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No vehicles recorded.</div>}
          {vehicles.map((v) => (
            <div className="ledger-row" key={v.uuid}>
              <span className={`badge badge-${v.status === 'disposed' ? 'mute' : 'ok'}`}>
                {v.status === 'disposed' ? 'DISPOSED' : 'ACTIVE'}
              </span>
              <div className="ledger-main">
                <div className="ledger-text">
                  <strong>{v.make_model}</strong>
                  {v.registration ? <span className="mono muted"> · {v.registration}</span> : null}
                  {v.colour ? <span className="muted"> · {v.colour}</span> : null}
                  {v.owner_user ? <span className="muted"> · {v.owner_user}</span> : null}
                </div>
                {(v.acquired_date || v.disposed_date) && (
                  <div className="ledger-time">
                    {v.acquired_date ? `acquired ${v.acquired_date}` : ''}
                    {v.disposed_date ? ` · disposed ${v.disposed_date}` : ''}
                  </div>
                )}
              </div>
              {v.status !== 'disposed' && (
                <button className="btn btn-ghost" type="button" style={{ alignSelf: 'center' }} onClick={() => dispose(v.uuid, v.make_model)}>Mark disposed</button>
              )}
              <Attachments entityType="vehicle" entityUuid={v.uuid} />
            </div>
          ))}
        </div>
        <div className="panel-foot">Keep this list up to date — acquired or disposed vehicles should be recorded promptly.</div>
      </div>
    </Page>
  );
}

function AddForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ make_model: '', registration: '', colour: '', owner_user: '', acquired_date: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!f.make_model.trim()) { setErr('Enter the make and model.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/vehicles', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          make_model: f.make_model,
          ...(f.registration ? { registration: f.registration } : {}),
          ...(f.colour ? { colour: f.colour } : {}),
          ...(f.owner_user ? { owner_user: f.owner_user } : {}),
          ...(f.acquired_date ? { acquired_date: f.acquired_date } : {}),
          ...(f.notes ? { notes: f.notes } : {}),
        }),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed'); return; }
      onDone();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }

  return (
    <form className="card card-pad" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15 }}>Add a vehicle</h3>
      <label className="fld"><span>Make &amp; model <span style={{ color: 'var(--bad)' }}>*</span></span>
        <input value={f.make_model} onChange={(e) => setF({ ...f, make_model: e.target.value })} placeholder="e.g. Ford Focus 1.5 TDCi" />
      </label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Registration (optional)</span>
          <input value={f.registration} onChange={(e) => setF({ ...f, registration: e.target.value })} placeholder="AB12 CDE" />
        </label>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Colour (optional)</span>
          <input value={f.colour} onChange={(e) => setF({ ...f, colour: e.target.value })} placeholder="Silver" />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Owner / registered user (optional)</span>
          <input value={f.owner_user} onChange={(e) => setF({ ...f, owner_user: e.target.value })} placeholder="Self" />
        </label>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Acquired date (optional)</span>
          <input type="date" value={f.acquired_date} onChange={(e) => setF({ ...f, acquired_date: e.target.value })} />
        </label>
      </div>
      <label className="fld"><span>Notes (optional)</span>
        <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Any relevant details" />
      </label>
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{err}</div>}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 6 }}>{busy ? 'Saving…' : 'Save vehicle'}</button>
    </form>
  );
}
